import { useEffect, useMemo, useState } from "react";
import CarriedClearReview from "./CarriedClearReview";
import CollapsibleSection from "./CollapsibleSection";
import ColumnFilterGrid from "./ColumnFilterGrid";
import ConfirmDangerAction from "./ConfirmDangerAction";
import DataTable from "./DataTable";
import ForgetUndoBanner from "./ForgetUndoBanner";
import InfoTip from "./InfoTip";
import { api } from "../api/client";
import { useToast } from "./Toast";
import { sigLabel } from "../lib/manualMatchLabels";
import {
  CARRIED_SIG, carriedConfig, carriedRows, carriedSourceColumns, sliceBySigs, type Book,
} from "../lib/priorPeriodMatch";
import {
  carriedBookOf, carriedPairKey, carriedRowSigs, crossListSigs, isCarriedOffset, isCrossListPair,
} from "../types/api";
import type {
  CarriedMatchOutcome, CarriedMatchPair, CarriedMatchUnresolved, FrameRecords,
  SideDisplayConfig, UnreconciledBlock,
} from "../types/api";

const BOOKS: Book[] = ["insurer", "cri"];
const listName = (b: Book) => (b === "cri" ? "CRI" : "insurer");

interface Props {
  block: UnreconciledBlock;
  insurerName: string;
  // This period's frames, read ONLY to name a pair's settling rows.
  criFrame: FrameRecords;
  insurerFrame: FrameRecords;
  criConfig?: SideDisplayConfig;
  insConfig?: SideDisplayConfig;
  // Bumped by the match bar after it saves a pair, so the store is read back.
  storeVersion: number;
  busy: boolean;
  // Re-run the reconciliation: the Adjusted Closing is an ENGINE figure.
  onChanged: () => Promise<void> | void;
}

// The prior-period half of the Manual Match tab, below the grids: the remembered
// prior-period matches of BOTH carried lists (the on-disk per-insurer store, auto-applied
// on every upload) with what each did this run, the ones that did not settle, and the
// review of automatic clears (Reject / Restore). Matching itself is in the books above.
export default function PriorPeriodMatches({ block, insurerName, criFrame, insurerFrame,
                                            criConfig, insConfig, storeVersion, busy: outerBusy,
                                            onChanged }: Props) {
  const toast = useToast();
  const [ownBusy, setBusy] = useState(false);
  const busy = outerBusy || ownBusy;
  const [error, setError] = useState("");

  const rowsOf = (b: Book) => carriedRows(block, b);
  const configs = useMemo(() => ({
    insurer: carriedConfig(carriedRows(block, "insurer"), carriedSourceColumns(block, "insurer")),
    cri: carriedConfig(carriedRows(block, "cri"), carriedSourceColumns(block, "cri")),
  }), [block]);
  const booksPresent = BOOKS.filter((b) => rowsOf(b).total_rows > 0);

  const [remembered, setRemembered] = useState<CarriedMatchPair[]>([]);
  const [rememberedError, setRememberedError] = useState("");
  useEffect(() => {
    let stale = false;
    api.carriedMatches(insurerName)
      .then((r) => { if (!stale) { setRemembered(r.pairs); setRememberedError(""); } })
      // NOT "none saved": the store could not be read, so what it holds is unknown, and
      // the engine still applies whatever is in there.
      .catch((e) => { if (!stale) setRememberedError(e instanceof Error ? e.message : String(e)); });
    return () => { stale = true; };
  }, [insurerName, block, storeVersion]);
  // What the last Forget removed and where each pair sat in the store, held so it can be
  // put back exactly (see restoreForgotten).
  const [forgotten, setForgotten] = useState<{ pair: CarriedMatchPair; position: number }[] | null>(null);
  useEffect(() => { setForgotten(null); }, [insurerName, storeVersion]);

  // What each stored pair DID this run, from the engine, per list.
  const outcomes = useMemo(() => ({
    insurer: {
      applied: (block.manual_applied ?? []) as CarriedMatchOutcome[],
      untied: (block.manual_untied ?? []) as CarriedMatchOutcome[],
      unresolved: (block.manual_unresolved ?? []) as CarriedMatchUnresolved[],
    },
    cri: {
      applied: (block.cri_manual_applied ?? []) as CarriedMatchOutcome[],
      untied: (block.cri_manual_untied ?? []) as CarriedMatchOutcome[],
      unresolved: (block.cri_manual_unresolved ?? []) as CarriedMatchUnresolved[],
    },
  }), [block]);
  const unresolvedByKey = useMemo(() => {
    const m = new Map<string, CarriedMatchUnresolved>();
    for (const b of BOOKS) for (const u of outcomes[b].unresolved) m.set(carriedPairKey(u.pair), u);
    return m;
  }, [outcomes]);
  // Outcomes name the saved pair that produced them, so one is read PER PAIR: judged per
  // item, a pair that lost to an earlier one naming the same item read "Settled".
  const byPair = useMemo(() => {
    const all = BOOKS.flatMap((b) => [...outcomes[b].applied, ...outcomes[b].untied]);
    if (!all.every((o) => o.pair)) return null;
    return {
      applied: new Set(BOOKS.flatMap((b) => outcomes[b].applied.map((a) => carriedPairKey(a.pair!)))),
      untied: new Set(BOOKS.flatMap((b) => outcomes[b].untied.map((u) => carriedPairKey(u.pair!)))),
    };
  }, [outcomes]);

  // Where a match's rows rolled forward into the prior-period lists, the items they are now.
  const rolledOf = useMemo(() => {
    const m = new Map<string, NonNullable<CarriedMatchPair["rolled"]>>();
    for (const b of BOOKS) {
      for (const o of [...outcomes[b].applied, ...outcomes[b].untied]) {
        if (o.pair?.rolled) m.set(carriedPairKey(o.pair), o.pair.rolled);
      }
    }
    return m;
  }, [outcomes]);

  // The item(s) a non-cross-list pair names on its own list: an offset's, several items
  // settled by rows together, or the one item.
  const itemsOf = (p: CarriedMatchPair): string[] =>
    (p.carried_sigs?.length ?? 0) >= 2 ? p.carried_sigs! : [p.carried_sig ?? ""];
  // A stored pair's own item(s), per list.
  const legsOf = (p: CarriedMatchPair): [Book, string][] =>
    p.basket
      ? BOOKS.flatMap((b) => (p.basket!.items[b] ?? []).map((s): [Book, string] => [b, s]))
      : isCrossListPair(p)
      ? [...crossListSigs(p).ins.map((s): [Book, string] => ["insurer", s]),
         ...crossListSigs(p).cri.map((s): [Book, string] => ["cri", s])]
      : itemsOf(p).map((s): [Book, string] => [carriedBookOf(p) as Book, s]);
  const outcomeOf = (p: CarriedMatchPair) => {
    const key = carriedPairKey(p);
    const why = unresolvedByKey.get(key);
    const legs = legsOf(p);
    // A result built before outcomes named their pair falls back to the per-item reading.
    const hit = (k: "applied" | "untied", b: Book, s: string) =>
      outcomes[b][k].some((o) => o.carried_sig === s);
    const settled = byPair ? byPair.applied.has(key)
      : legs.length > 0 && legs.every(([b, s]) => hit("applied", b, s));
    const missed = !settled && !why && (byPair ? byPair.untied.has(key)
      : legs.some(([b, s]) => hit("untied", b, s)));
    // Tied against rows dated after the period: the item stays open, on the Cleared line.
    const later = BOOKS.flatMap((b) => outcomes[b].applied)
      .find((o) => o.cleared_after && o.pair && carriedPairKey(o.pair) === key)?.cleared_after;
    return { settled, missed, why,
             status: later ? `Cleared in ${later.cleared_in || "a later month"}`
               : settled ? "Settled this run" : missed ? "Amounts differ — not settled"
               : "Not applied this run" };
  };

  const itemLabel = (b: Book, s: string) => sigLabel(rowsOf(b), s, configs[b], CARRIED_SIG);
  // The prior-period items a pair's rows rolled forward into, else undefined. A same-ledger
  // pair's list also holds the pair's own item, which is not one of its rows.
  const rolledRows = (p: CarriedMatchPair): string[] | undefined => {
    const r = rolledOf.get(carriedPairKey(p));
    if (!r) return undefined;
    const side: Book = p.side === "cri" ? "cri" : "insurer";
    const sigs = [...(r[side] ?? [])];
    if (carriedBookOf(p) !== side) return sigs;
    for (const s of itemsOf(p)) {
      const own = sigs.indexOf(s);
      if (own >= 0) sigs.splice(own, 1);
    }
    return sigs;
  };
  const rowLabel = (p: CarriedMatchPair) => {
    const isCri = p.side === "cri";
    const rolled = rolledRows(p);
    if (rolled) return rolled.map((s) => itemLabel(isCri ? "cri" : "insurer", s)).join("  +  ");
    return carriedRowSigs(p)
      .map((s) => sigLabel(isCri ? criFrame : insurerFrame, s, isCri ? criConfig : insConfig))
      .join("  +  ");
  };

  // A basket's legs per list and per ledger, each named by its row.
  const basketParts = (p: CarriedMatchPair) => p.basket
    ? [...BOOKS.filter((b) => p.basket!.items[b]?.length).map((b) => ({
        badge: `${listName(b)} list`,
        text: p.basket!.items[b]!.map((s) => itemLabel(b, s)).join("  +  ") })),
       ...BOOKS.filter((b) => p.basket!.rows[b]?.length).map((b) => ({
        badge: b === "cri" ? "CRI" : "Insurer",
        text: p.basket!.rows[b]!.map((s) => sigLabel(b === "cri" ? criFrame : insurerFrame, s,
                                                      b === "cri" ? criConfig : insConfig))
          .join("  +  ") }))]
    : [];

  // Name a stored pair by its rows, not its raw signatures, with the list it sits on.
  function PairLabel({ pair }: { pair: CarriedMatchPair }) {
    if (pair.basket) {
      return (
        <>
          {basketParts(pair).map((x, i) => (
            <span key={x.badge}>
              {i > 0 && " ⟷ "}
              <span className="badge" style={{ margin: "0 4px" }}>{x.badge}</span>
              {x.text || "—"}
            </span>
          ))}
        </>
      );
    }
    if (isCrossListPair(pair)) {
      const { ins, cri } = crossListSigs(pair);
      return (
        <>
          <span className="badge" style={{ marginRight: 4 }}>Insurer list</span>
          {ins.map((s) => itemLabel("insurer", s)).join("  +  ")}
          {" ⟷ "}
          <span className="badge" style={{ margin: "0 4px" }}>CRI list</span>
          {cri.map((s) => itemLabel("cri", s)).join("  +  ")}
        </>
      );
    }
    const b = carriedBookOf(pair) as Book;
    const listBadge = <span className="badge" style={{ marginRight: 4 }}>{b === "cri" ? "CRI list" : "Insurer list"}</span>;
    if (isCarriedOffset(pair)) {
      return (
        <>
          {listBadge}
          <span className="badge" style={{ marginRight: 6 }}>
            Offset · {(pair.carried_sigs ?? []).length} items
          </span>
          {(pair.carried_sigs ?? []).map((s) => itemLabel(b, s)).join("  +  ")}
        </>
      );
    }
    return (
      <>
        {listBadge}
        {itemsOf(pair).map((s) => itemLabel(b, s)).join("  +  ")}
        {" ⟷ "}
        <span className="badge" style={{ margin: "0 4px" }}>
          {pair.side === "cri" ? "CRI" : "Insurer"}
          {rolledRows(pair) ? " list · rolled forward" : ""}
        </span>
        {rowLabel(pair) || "—"}
      </>
    );
  }

  // Plain text for a stored pair, so the table can be searched and sorted on it.
  function pairText(p: CarriedMatchPair): string {
    if (p.basket) return basketParts(p).map((x) => `${x.badge} ${x.text}`).join(" ⟷ ");
    if (isCrossListPair(p)) {
      const { ins, cri } = crossListSigs(p);
      return `Insurer list ${ins.map((s) => itemLabel("insurer", s)).join(" + ")} ⟷ `
        + `CRI list ${cri.map((s) => itemLabel("cri", s)).join(" + ")}`;
    }
    const b = carriedBookOf(p) as Book;
    if (isCarriedOffset(p)) {
      return `${listName(b)} list ` + (p.carried_sigs ?? []).map((s) => itemLabel(b, s)).join(" + ");
    }
    return `${listName(b)} list ${itemsOf(p).map((s) => itemLabel(b, s)).join(" + ")} ⟷ ${rowLabel(p)}`;
  }

  // Every leg of a stored pair with ALL its columns, so a match can be checked in place.
  function PairDetail({ pair }: { pair: CarriedMatchPair }) {
    const parts: { title: string; frame: FrameRecords; config?: SideDisplayConfig }[] = [];
    // Its rows rolled forward: every leg is a prior-period item now, so list those.
    const rolled = rolledOf.get(carriedPairKey(pair));
    const byList = new Map<Book, string[]>();
    if (rolled) {
      for (const b of BOOKS) if (rolled[b]?.length) byList.set(b, rolled[b]!);
    } else {
      for (const [b, s] of legsOf(pair)) byList.set(b, [...(byList.get(b) ?? []), s]);
    }
    const rowsList: Book = pair.side === "cri" ? "cri" : "insurer";
    for (const [b, sigs] of byList) {
      parts.push({ title: rolled && b === rowsList
                     ? `Prior-period ${listName(b)} list (the matched rows rolled forward here)`
                     : `Prior-period ${listName(b)} list`,
                   frame: sliceBySigs(rowsOf(b), CARRIED_SIG, sigs), config: configs[b] });
    }
    if (!rolled && pair.basket) {
      for (const b of BOOKS) {
        if (!pair.basket.rows[b]?.length) continue;
        parts.push({ title: b === "cri" ? "CRI ledger — this period" : `${insurerName || "Insurer"} ledger — this period`,
                     frame: sliceBySigs(b === "cri" ? criFrame : insurerFrame, "_sig", pair.basket.rows[b]!),
                     config: b === "cri" ? criConfig : insConfig });
      }
    } else if (!rolled && !isCrossListPair(pair) && !isCarriedOffset(pair)) {
      const isCri = pair.side === "cri";
      parts.push({ title: isCri ? "CRI ledger — this period" : `${insurerName || "Insurer"} ledger — this period`,
                   frame: sliceBySigs(isCri ? criFrame : insurerFrame, "_sig", carriedRowSigs(pair)),
                   config: isCri ? criConfig : insConfig });
    }
    return (
      <div style={{ margin: "6px 0 10px" }}>
        {parts.map((p) => (
          <div key={p.title} style={{ marginBottom: 6 }}>
            <p className="kpi-label" style={{ textAlign: "left", margin: "0 0 4px" }}>{p.title}</p>
            {p.frame.total_rows === 0
              ? <p className="muted">Not in the files uploaded this run.</p>
              : <DataTable frame={p.frame} config={p.config} pageSize={10} />}
          </div>
        ))}
      </div>
    );
  }

  // Forget stored pairs. The carried items revert to whatever the ladder concludes.
  async function forgetPairs(pairs: CarriedMatchPair[]) {
    if (!pairs.length) return;
    setBusy(true);
    setError("");
    try {
      const at = new Map(remembered.map((p, i) => [carriedPairKey(p), i]));
      const r = await api.removeCarriedMatches(insurerName, pairs);
      setRemembered(r.pairs);
      toast.success(pairs.length === 1
        ? "Match forgotten — recalculating."
        : `${pairs.length} matches forgotten — recalculating.`);
      await onChanged();
      // Offered back only once the removal actually landed (and after the re-run, which
      // re-reads the store and would otherwise drop the offer).
      setForgotten(pairs.map((p) => ({ pair: p, position: at.get(carriedPairKey(p)) ?? remembered.length })));
    } catch (e) {
      setError("Could not update saved matches: " + String(e));
    } finally {
      setBusy(false);
    }
  }

  // Put back what the last Forget removed. The pairs are re-sent AS STORED: each already
  // carries its own `book`. One at a time in position order, each at its old position:
  // the first saved pair naming an item wins, so appending them would change which pair
  // settles it.
  async function restoreForgotten(entries: { pair: CarriedMatchPair; position: number }[]) {
    if (!entries.length) return;
    setBusy(true);
    setError("");
    try {
      const ordered = [...entries].sort((a, b) => a.position - b.position);
      const stillGone: typeof entries = [];
      let last: CarriedMatchPair[] | null = null;
      for (const e of ordered) {
        try {
          last = (await api.addCarriedMatch(insurerName, e.pair, e.position)).pairs;
        } catch {
          stillGone.push(e);
        }
      }
      if (last) setRemembered(last);
      if (stillGone.length) {
        toast.error(stillGone.length === 1
          ? "1 match could not be restored. The rest are back — it is still offered below."
          : `${stillGone.length} matches could not be restored. The rest are back — they are still offered below.`);
      } else {
        toast.success(entries.length === 1
          ? "Match restored — recalculating."
          : `${entries.length} matches restored — recalculating.`);
      }
      if (last) await onChanged();
      // Keep offering exactly what is still missing, so a retry can neither re-add a
      // restored pair nor overstate the loss.
      setForgotten(stillGone.length ? stillGone : null);
    } finally {
      setBusy(false);
    }
  }

  const [rememberedView, setRememberedView] = useState<"list" | "table">("list");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const ordered = useMemo(() => {
    const withOutcome = remembered.map((p) => ({ p, key: carriedPairKey(p), ...outcomeOf(p) }));
    // The ones that need a decision first: a saved match that moves nothing must not
    // hide among the working ones.
    return [...withOutcome.filter((x) => !x.settled), ...withOutcome.filter((x) => x.settled)];
  }, [remembered, outcomes, unresolvedByKey, byPair]);
  const nAttention = ordered.filter((x) => !x.settled).length;
  const untied = BOOKS.flatMap((b) => outcomes[b].untied.map((u) => ({ b, u })));

  // Folded like the main lists, but opened by itself whenever a saved match did not
  // settle or the store could not be read: it moves nothing while it looks saved.
  const attention = nAttention > 0 || untied.length > 0 || rememberedError !== "";
  const [open, setOpen] = useState(false);
  useEffect(() => { if (attention) setOpen(true); }, [attention]);

  return (
    <CollapsibleSection title={`Prior-period matches for ${insurerName}`} className="mm-section"
      open={open} onOpenChange={setOpen}
      badge={rememberedError ? "could not load" : nAttention ? `${nAttention} not settled` : undefined}
      badgeTone="in"
      sub={`${remembered.length} match${remembered.length === 1 ? "" : "es"}`}
      actions={open && remembered.length > 0 ? (
        <span style={{ display: "inline-flex", gap: 4 }}>
          {(["list", "table"] as const).map((v) => (
            <button key={v} className={rememberedView === v ? "btn btn-sm btn-primary" : "btn btn-sm"}
              onClick={() => setRememberedView(v)}>
              {v === "list" ? "List" : "Table"}
            </button>
          ))}
        </span>
      ) : undefined}>
      {error && <div className="badge badge-risk" style={{ display: "block", marginBottom: 8 }}>{error}</div>}

      {/* Stored matches that resolved but did NOT tie: they settle nothing (no figure
          moved), and "saved but had no effect" must not look like "saved and working". */}
      {untied.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p className="kpi-label" style={{ textAlign: "left" }}>
            Saved matches that did not settle ({untied.length})
          </p>
          <p className="muted" style={{ margin: "0 0 8px", fontSize: "0.78rem" }}>
            Both rows were found, but they do not settle: the amounts differ by more than
            the tolerance, or the signs do not mirror. Nothing settled and no figure moved —
            each item stays in the still-open total, annotated with the reason.
          </p>
          {untied.map(({ b, u }, i) => (
            <div key={i} className="carried-pair-row">
              <span className="muted carried-pair-label">
                <span className="badge" style={{ marginRight: 4 }}>{b === "cri" ? "CRI list" : "Insurer list"}</span>
                {itemLabel(b, u.carried_sig)}
              </span>
              <span className="badge badge-warn" style={{ whiteSpace: "nowrap" }}>
                {u.note ?? "did not tie"}
              </span>
            </div>
          ))}
        </div>
      )}

      <p className="muted" style={{ margin: "0 0 8px" }}>
        Saved prior-period matches auto-apply whenever this insurer is uploaded — items
        re-resolve by content (amounts included), so a changed figure simply stops applying
        and says so.
      </p>
      {rememberedError && (
        // NOT "none saved": what the store holds is unknown, and the engine still
        // applies whatever is in there.
        <p className="muted" style={{ color: "var(--risk)" }}>
          Could not load the saved matches for this insurer, so this list may be
          incomplete. Any saved match is still being applied to the figures above.
          Reload the page to try again. ({rememberedError})
        </p>
      )}
      {/* OUTSIDE the list conditional below: forgetting them all empties that list, and
          that is precisely when the undo has to be on screen. */}
      {forgotten && (
        <ForgetUndoBanner count={forgotten.length} disabled={busy}
          onUndo={() => restoreForgotten(forgotten)} />
      )}
      {remembered.length === 0 ? (
        !rememberedError && <p className="muted">None saved yet — matching a prior-period item above remembers it.</p>
      ) : (
        <>
          {rememberedView === "table" ? (
            <CarriedPairTable entries={ordered.map((x) => ({
                key: x.key, pair: x.p, status: x.status, reason: x.why?.reason ?? "",
                labelText: pairText(x.p),
              }))}
              busy={busy} insurerName={insurerName} resetKey={`${insurerName}|prior|remembered`}
              onForget={(pairs) => forgetPairs(pairs)} />
          ) : ordered.map(({ p, key, settled, missed, why, status }) => (
            <div key={key}>
              <div className="carried-pair-row">
                <span className="muted carried-pair-label">
                  <PairLabel pair={p} />
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
                  <span className={settled ? "badge badge-success" : "badge badge-warn"}>{status}</span>
                  {!settled && (
                    <InfoTip text={why
                      ? `${why.reason}. It stays saved and re-applies as soon as both rows appear again.`
                      : missed
                        ? "Everything was found but it does not settle, so nothing settled. The items stay open, annotated with the gap: listed together with their rows on Group 3 (Figure / Balance Mismatch), or left on their own group line when the rows are dated after the period."
                        : "This saved match did not apply to the files uploaded this time. It stays saved and re-applies when the rows appear again."} />
                  )}
                  <button className="btn" aria-expanded={expanded.has(key)}
                    onClick={() => setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      return next;
                    })}>
                    {expanded.has(key) ? "Hide rows" : "Rows"}
                  </button>
                  <button className="btn" disabled={busy} onClick={() => forgetPairs([p])}>
                    Forget
                  </button>
                </span>
              </div>
              {expanded.has(key) && <PairDetail pair={p} />}
            </div>
          ))}
          {/* Two-step and typed: this erases every saved prior-period pairing for this
              insurer permanently and sits right under a column of single Forget buttons. */}
          <ConfirmDangerAction
            label="Forget all remembered matches"
            disabled={busy}
            title={`Forget all ${remembered.length} remembered prior-period match${remembered.length === 1 ? "" : "es"} for ${insurerName}?`}
            body={<>
              Every saved prior-period pairing for this insurer, on both lists, is deleted
              from the store — including ones built in earlier months — and none of them
              will auto-apply on future uploads. Any applied in this run stop settling
              immediately. <strong>Undo is offered here only until your next change on this
              tab, and never after a reload — after that it cannot be reversed.</strong> To
              remove just one, use its own Forget button above.
            </>}
            confirmWord="FORGET"
            confirmLabel={`Forget all ${remembered.length}`}
            onConfirm={() => forgetPairs(remembered)} />
        </>
      )}

      {/* The Release equivalent: an AUTOMATIC clear finance disagrees with is rejected
          (held open), and a rejection can be restored. One review per carried list. */}
      {booksPresent.map((b) => (
        <CarriedClearReview key={b} rows={rowsOf(b)} config={configs[b]} book={b}
          insurerName={insurerName} onChanged={onChanged} />
      ))}
    </CollapsibleSection>
  );
}

// The remembered list as a TABLE: search, filter, select-all-shown and a bulk Forget that
// only ever touches matches still visible (the main tab's MatchListTable contract).
interface PairEntry {
  key: string;
  pair: CarriedMatchPair;
  status: string;
  reason: string;
  labelText: string;
}

function CarriedPairTable({ entries, busy, insurerName, resetKey, onForget }: {
  entries: PairEntry[]; busy: boolean; insurerName: string; resetKey: string;
  onForget: (pairs: CarriedMatchPair[]) => void;
}) {
  const frame: FrameRecords = useMemo(() => ({
    columns: ["_k", "Match #", "Status", "Match", "Reason"],
    rows: entries.map((e, i) => [e.key, i + 1, e.status, e.labelText, e.reason || null]),
    indices: entries.map((_, i) => i),
    total_rows: entries.length,
  }), [entries]);
  const config: SideDisplayConfig = { columns: ["Match #", "Status", "Match", "Reason"], renames: {} };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [visible, setVisible] = useState<string[] | null>(null);
  useEffect(() => {
    const have = new Set(entries.map((e) => e.key));
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => have.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [entries]);
  const shown = visible ? new Set(visible) : null;
  const acting = entries.filter((e) => selected.has(e.key) && (!shown || shown.has(e.key)));
  const hidden = selected.size - acting.length;
  const setMany = (keys: string[], on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    for (const k of keys) { if (on) next.add(k); else next.delete(k); }
    return next;
  });
  const n = acting.length;
  return (
    <div>
      <div className="msearch-actions">
        <ConfirmDangerAction label={`Forget ${n} selected`} disabled={busy || n === 0}
          title={`Forget ${n} remembered match${n === 1 ? "" : "es"} for ${insurerName}?`}
          body="They are deleted from the store and stop auto-applying. Undo is offered only until your next change on this tab."
          confirmWord="FORGET" confirmLabel={`Forget ${n}`}
          onConfirm={() => {
            setSelected(new Set());
            onForget(acting.map((e) => e.pair));
          }} />
        {hidden > 0 && (
          <span className="muted">
            {hidden} ticked match{hidden === 1 ? "" : "es"} hidden by the search or filters
            {hidden === 1 ? " is" : " are"} left alone.
          </span>
        )}
      </div>
      <ColumnFilterGrid frame={frame} sigColumn="_k"
        selectedSig={null} selectedSigs={selected}
        onSelect={(k) => k && setMany([k], !selected.has(k))}
        onSelectMany={setMany} onFilteredChange={setVisible}
        config={config} resetKey={resetKey} compact title="Remembered prior-period matches" />
    </div>
  );
}
